import React, { useEffect, useRef } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Button as UIButton } from '@repo/ui'
import { PlusIcon } from 'lucide-react'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { SortField } from 'components/UI/SortField'
import { FilterSheetFooter } from 'components/UI/FilterSheetFooter'
import { SectionLabel } from 'components/UI/SectionLabel'
import { sheetStepSx } from 'utils/pageTransition'
import { useAutoApply } from 'hooks/useAutoApply'

import type { SortFieldType } from '@repo/ui'
import { MENU_ROW_LABEL_SX, menuRowToneSx } from 'components/UI/menuRowTone'

/**
 * One sortable field. `type` is REQUIRED, and that is the entire point.
 *
 * `SortField`'s own `fieldTypes` prop is OPTIONAL and degrades silently to
 * "A - Z" / "Z - A" when omitted. That silence is what let the VERA-480 date-label
 * fix land on exactly ONE of the four sort surfaces and go unnoticed on the other
 * three: Provenance sorted `date` and `sell_price` — a CURRENCY — and Activity
 * sorted `date`, all offering alphabetical labels for values that are not
 * alphabetical. Nothing warned, because degrading to text is legitimate for a
 * text field.
 *
 * Binding the type to the field DECLARATION rather than to a parallel map means a
 * surface cannot register a sortable field without saying what kind of value it
 * is. The map was the drift; there is now no map to forget.
 */
export type SortOption<F extends string = string> = {
  key: F
  label: string
  type: SortFieldType
}

export type SortEntry<F extends string = string> = {
  field: F
  direction: 'ASC' | 'DESC'
}

export type SortDrawerProps<F extends string = string> = {
  open: boolean
  onClose: () => void
  /** `pick` = choose the first property; `configure` = set directions, stack more. */
  step: 'pick' | 'configure'
  setStep: (step: 'pick' | 'configure') => void
  /** This surface's sortable fields. Order here is the order shown. */
  options: SortOption<F>[]
  primaryField: F
  primaryDirection: 'ASC' | 'DESC'
  additionalSorts: SortEntry<F>[]
  onPrimaryFieldChange: (field: F) => void
  onPrimaryDirectionChange: (direction: 'ASC' | 'DESC') => void
  /**
   * Clear every sort on the CALLER's side. This is the commit for the emptied
   * sheet — the debounced commit calls it instead of `onApply` when the sheet
   * holds no primary sort. It must not close the sheet.
   */
  onClearAll: () => void
  /**
   * Commit the sheet's sorts. RECEIVES the primary — it must not be read back
   * from the caller's state.
   *
   * The commit calls `onPrimaryFieldChange` immediately before this, and React
   * has not flushed that state by the time this runs, so a caller reading its own
   * `sortField` here gets the PREVIOUS one. Measured: picking "Ownership date"
   * and committing sorted the list alphabetically, because the value that reached
   * the query was still `name`. (The name is historic — there is no Apply button
   * any more; see the auto-apply note on the component.)
   */
  onApply: (primary: SortEntry<F>) => void
  /**
   * Commit the additional sorts. ONE callback, fired by the debounced commit —
   * not three granular ones called as you edit. The granular trio wrote through
   * on every interaction with no coalescing, so building a three-level sort cost
   * three re-sorts of the list.
   */
  onAdditionalChange: (next: SortEntry<F>[]) => void
  /** Sorts allowed BEYOND the primary. Defaults to 2 (three total). */
  maxAdditionalSorts?: number
}

/**
 * The sort bottom sheet for every list in the app.
 *
 * Replaces four implementations — `ScansSortDrawer` (180 lines),
 * `ProvenanceSortDrawer` (159), `ActivitySortDrawer` (203), and the certificates
 * sort inlined in `pages/certificates/index.tsx`. Diffed against each other, the
 * three subview drawers differed only in their field list and the missing type
 * map above; everything else that differed between them was semicolons, import
 * order, and `type` vs `interface`.
 *
 * ## Certificates is the template, and the subviews were the poorer copy
 *
 * The certificates sheet was NOT equivalent to the other three — it was strictly
 * richer, and collapsing everything onto the simpler majority would have been a
 * regression on the one surface that had it right. It alone had precedence
 * badges, a Reset All / Apply(n) footer, and a separate field-picker for adding
 * a sort. The other three had a single "Clear all" that cleared, applied, AND
 * closed in one press.
 *
 * This mirrors the FILTER sheets exactly, which were unified earlier in this epic
 * on an explicit directive — "all filter drawers should operate the same way, use
 * the certificates page one" — against the same divergence table. So the footer
 * here IS `FilterSheetFooter`, the same component the filter sheets use.
 *
 * ## AUTO-APPLY (and what that reverses)
 *
 * Every control in this sheet commits itself on a 400ms debounce; there is no
 * Apply button at either breakpoint, matching the filter sheets and the platform's
 * filter drawer. Reset therefore DOES commit now — that is a deliberate reversal
 * of the rule this file used to carry, and the reasoning behind that rule went
 * with the button: "a Reset that silently commits breaks the promise an explicit
 * Apply makes" is only an argument while there IS an explicit Apply. With none,
 * clearing is simply the change, and refusing to commit it would leave the sheet
 * with no way to reach "no sort" at all.
 *
 * §36: the second hand-roll is a shared component. This was the fourth.
 */
export function SortDrawer<F extends string = string>({
  open,
  onClose,
  step,
  setStep,
  options,
  primaryField,
  primaryDirection,
  additionalSorts,
  onPrimaryFieldChange,
  onPrimaryDirectionChange,
  onClearAll,
  onApply,
  onAdditionalChange,
  maxAdditionalSorts = 2,
}: SortDrawerProps<F>) {
  /*
   * The step change PUSHES within the sheet instead of cutting to it.
   *
   * Tapping a field replaced the sheet's whole contents in one frame, which
   * reads as a second drawer appearing rather than as this one advancing — and
   * the two steps are the same sheet, one screen deeper.
   *
   * Same convention as page navigation: forward comes in from the RIGHT, back
   * from the LEFT (see usePushPage). So a sheet that goes a level deeper moves
   * the way a page that goes a level deeper moves, and the gesture is learned
   * once.
   *
   * Only the CONTENT slides. The sheet itself is content-height, so its height
   * still changes between the two steps and that change is not animated —
   * `height: auto` cannot be transitioned, and the measured-height approach was
   * tried and reverted for mispositioning the sheet and clipping its footer.
   * The horizontal push is the part that was cheap and safe; the height jump is
   * knowingly still there.
   */
  const prevStep = useRef(step)
  const enterFrom = prevStep.current === step ? null : step === 'configure' ? 'right' : 'left'
  useEffect(() => {
    prevStep.current = step
  }, [step])

  /* The keyframes, the 16% offset and the reduced-motion guard now live in
     `utils/pageTransition` as `sheetStepSx` — this was the FIRST hand-roll, and
     ScanTag's "Where to scan" step (working-conventions §52) was the second, so
     §36 says it becomes shared rather than copied. Behaviour is unchanged. */
  const stepSx = sheetStepSx(enterFrom)

  /*
   * The add-a-field picker is a SECOND sheet over this one, and its state lives
   * here rather than in each caller. In the certificates page it was a caller-owned
   * `showAddSortOverlay` boolean threaded alongside the sheet's own open/step —
   * three pieces of state for one sheet, which is what made the block hard to lift
   * out in the first place. Nothing outside needs to know the picker is open.
   */
  const [addOpen, setAddOpen] = React.useState(false)

  /*
   * The sheet's working copy. It still exists, but it is no longer a STAGE-then-
   * Apply buffer — it is what the controls render from between the interaction and
   * the debounced commit 400ms later.
   *
   * The drafts remain useful for two reasons that survive auto-apply: they let a
   * multi-step edit (pick a field, flip its direction, add a second sort) coalesce
   * into ONE re-sort instead of three, and they let "no sort at all" be a
   * representable, committable state (`draftPrimary === null`) rather than
   * something the sheet has to bounce to another step to express.
   *
   * Re-seeded on every OPEN, so the sheet always reflects what is actually applied.
   * That re-seed is also exactly why `useAutoApply` is triggered by interaction
   * rather than by watching this state — a watcher would treat the re-seed as an
   * edit and commit the applied sort back to the page on every open.
   */
  const [draftPrimary, setDraftPrimary] = React.useState<SortEntry<F> | null>({
    field: primaryField,
    direction: primaryDirection,
  })
  const [draftAdditional, setDraftAdditional] = React.useState<SortEntry<F>[]>(additionalSorts)

  React.useEffect(() => {
    if (open) {
      setDraftPrimary({ field: primaryField, direction: primaryDirection })
      setDraftAdditional(additionalSorts)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /*
   * AUTO-APPLY — no Apply button. Note what it does NOT do any more: `onClose()`.
   * A commit that closed the sheet made sense when it was a deliberate press; fired
   * automatically it would dismiss the sheet out from under someone who has just
   * flipped a direction and is reaching for the next control.
   *
   * `open` is passed so a change made in the last 400ms before dismissal is flushed
   * rather than dropped — this sheet stays mounted between opens.
   */
  const { schedule } = useAutoApply(() => {
    if (draftPrimary) {
      onPrimaryFieldChange(draftPrimary.field)
      onPrimaryDirectionChange(draftPrimary.direction)
      onAdditionalChange(draftAdditional)
      onApply(draftPrimary)
    } else {
      // The sheet was emptied, so clearing IS the commit. This branch was written
      // long before it was reachable — the shared footer used to grey out the only
      // button that could get here.
      onClearAll()
    }
  }, open)

  const availableFields = options.map(o => o.key)
  const fieldTypes = React.useMemo(
    () => Object.fromEntries(options.map(o => [o.key, o.type])) as Record<string, SortFieldType>,
    [options],
  )
  // Same derivation as `fieldTypes`, for the same reason: SortField carries its
  // own label map, so without this a newly declared option renders its raw key.
  const fieldLabels = React.useMemo(
    () => Object.fromEntries(options.map(o => [o.key, o.label])) as Record<string, string>,
    [options],
  )

  const usedFields = [
    ...(draftPrimary ? [draftPrimary.field] : []),
    ...draftAdditional.map(s => s.field),
  ]
  const unusedOptions = options.filter(o => !usedFields.includes(o.key))
  const maxSortsReached = draftAdditional.length >= maxAdditionalSorts || unusedOptions.length === 0

  // A row of tappable field names. Used by BOTH the `pick` step and the add-picker,
  // which is the same list with a different destination.
  const fieldList = (list: SortOption<F>[], onPick: (key: F) => void) => (
    <Stack spacing={0}>
      {list.map(opt => (
        <Stack
          key={opt.key}
          direction='row'
          alignItems='center'
          role='button'
          tabIndex={0}
          onClick={() => onPick(opt.key)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onPick(opt.key)
            }
          }}
          sx={theme => ({
            cursor: 'pointer',
            px: 2,
            py: 2,
            minHeight: 52,
            borderRadius: '12px',
            /*
              The app's one menu-row tone (`menuRowTone.ts`): `grey[50]` behind the
              row AND the accent on the text, on hover and on `:focus-visible`
              alike. The fill was already right; the row just never changed its
              TEXT, so it stopped half way to what the Layout popover does.

              Picking a sort field is a one-shot action — the step closes — so
              every row is the unselected case and none takes the selected colour.
            */
            ...menuRowToneSx(theme),
          })}
        >
          <Typography sx={{ fontSize: 16, ...MENU_ROW_LABEL_SX }}>{opt.label}</Typography>
        </Stack>
      ))}
    </Stack>
  )

  return (
    <>
      <DrawerPopup
        title={step === 'pick' ? 'Sort' : 'Sort'}
        anchor={'bottom'}
        fullScreen={false}
        /* NO 'Cancel' — the header X that DrawerPopup draws by default. The sheet
           auto-applies, so there is nothing pending for a Cancel to discard, and the
           word would promise a revert to the sort you arrived with that this sheet
           can no longer perform. Reset clears to NO sort, which is a different
           outcome and is labelled as such. */
        handleOpen={open}
        handleClose={onClose}
        // Content-height, not full-page: a bottom sheet sized to 100vh covers the
        // whole screen for a few rows of options (VERA-294).
        paperSx={{ height: 'auto', maxHeight: '85vh' }}
        child={
          <Box sx={stepSx}>
            {step === 'pick' ? (
              <>
                {/*
                  NO section heading here, and that is a reversal of a change
                  made two commits ago.

                  The sheet read flat, so a `SORT BY` caption was added over the
                  field list to give the hierarchy something to lead with. It
                  competed with the sheet's own title instead: the header
                  already says "Sort", so the caption beneath it said the same
                  word twice for one list. That is the same redundancy argued
                  against for Profile's Notifications row — a heading that
                  restates what is already named adds a level and no
                  information.

                  The FILTER sheets keep their captions because they have
                  SEVERAL sections naming different axes (Event type, Date,
                  Owner, Status, Location), so each caption distinguishes one
                  from another. This sheet has one list, and the title is its
                  label.

                  The original complaint — that title and options sit at nearly
                  the same weight — is answered by the header's own divider and
                  the Cancel row separating them, not by repeating the word.
                */}
              {/* Only fields not already sorted on — picking one you are already
                  sorting by does nothing, so it should not be offered. */}
              {fieldList(unusedOptions, key => {
                setDraftPrimary({ field: key, direction: 'ASC' })
                setStep('configure')
                schedule()
              })}
              </>
            ) : (
              <Stack spacing={2}>
                {/* Each sort is its own block (field + direction + its own clear),
                    numbered so the precedence is readable rather than implied. */}
                <Stack spacing={3}>
                  {/*
                    Rendered only while a primary sort is DRAFTED, so a cleared sheet
                    looks cleared.

                    `draftPrimary` is seeded from the committed sort every time the sheet
                    opens, so `null` here means "the user explicitly cleared it", never
                    "nothing loaded yet". The old `?? primaryField` fallback therefore did
                    the wrong thing at exactly the moment it mattered: after Reset it
                    re-displayed the COMMITTED sort, so clearing looked like a no-op. That
                    is what made bouncing to the `pick` step feel necessary — and the
                    bounce is what stranded the user, because `pick` has no footer and
                    Apply was the only way to commit the clear.

                    With this guard the cleared state is representable in place: the Add
                    button and the footer remain, so you can pick a new sort or Apply the
                    empty one. `commit()` already had the branch for it (`onClearAll()`);
                    it was simply unreachable.
                  */}
                  {draftPrimary && (
                  <SortField
                    field={draftPrimary.field}
                    direction={draftPrimary.direction}
                    mobileMode
                    sortNumber={1}
                    availableFields={availableFields}
                    fieldTypes={fieldTypes}
                    fieldLabels={fieldLabels}
                    disabledFields={draftAdditional.map(s => s.field)}
                    onFieldChange={field => {
                      setDraftPrimary(p => ({ field: field as F, direction: p?.direction ?? 'ASC' }))
                      schedule()
                    }}
                    onDirectionChange={direction => {
                      setDraftPrimary(p => ({ field: p?.field ?? primaryField, direction }))
                      schedule()
                    }}
                    onClear={() => {
                      // Stays on `configure` for the same reason Reset does — see the
                      // footer below. Clearing the primary row and clearing via Reset are
                      // the same outcome ("no sort"), so they must not strand the user in
                      // two different ways.
                      setDraftPrimary(null)
                      setDraftAdditional([])
                      schedule()
                    }}
                  />
                  )}

                  {draftAdditional.map((sort, index) => {
                    // Every field already in use is unpickable here EXCEPT this
                    // row's own, which must stay selected in its own dropdown.
                    const disabledForThisRow = usedFields.filter((f, i) => {
                      if (i === index + 1) return false
                      return f !== sort.field
                    })
                    return (
                      <SortField
                        key={index}
                        field={sort.field}
                        direction={sort.direction}
                        mobileMode
                        sortNumber={index + 2}
                        availableFields={availableFields}
                        fieldTypes={fieldTypes}
                        fieldLabels={fieldLabels}
                        disabledFields={disabledForThisRow}
                        onFieldChange={field => {
                          setDraftAdditional(prev =>
                            prev.map((s, i) => (i === index ? { ...s, field: field as F } : s)),
                          )
                          schedule()
                        }}
                        onDirectionChange={direction => {
                          setDraftAdditional(prev =>
                            prev.map((s, i) => (i === index ? { ...s, direction } : s)),
                          )
                          schedule()
                        }}
                        onClear={() => {
                          setDraftAdditional(prev => prev.filter((_, i) => i !== index))
                          schedule()
                        }}
                      />
                    )
                  })}
                </Stack>

                {/*
                  ADD LIVES IN THE FOOTER, as the primary, paired with Reset on
                  one row — the left/right footer every other drawer in this app
                  uses. It used to be a full-width `secondary` stacked ABOVE the
                  footer, which made the sheet end in two full-width buttons and
                  read as two separate decisions rather than one footer.

                  This is the one footer here with TWO buttons, and that does not
                  contradict the auto-apply rule: the rule removed APPLY, because
                  a sheet that commits on interaction has nothing to commit. Add
                  is not a commit — it opens the field picker — so the slot is
                  free for it, and "primary" is honest because adding a sort is
                  the only forward action the step offers.
                */}
                <FilterSheetFooter
                  onApply={!maxSortsReached ? () => setAddOpen(true) : undefined}
                  applyLabel={
                    <>
                      <PlusIcon size={18} style={{ marginRight: 8 }} />
                      Add
                    </>
                  }
                  /* RESET ONLY — omitting `onApply` selects the shared component's
                     auto-apply mode, and `count` went with the button it labelled.
                     Reset now COMMITS: "no sort" is a real state (the list falls back
                     to its default order), and under auto-apply there is no Apply left
                     to reach the commit's clearing branch.

                     It STAYS on this step. It used to `setStep('pick')`, which was a
                     dead end: `pick` has no footer, so after Reset there was no Apply
                     to press and an applied sort survived. */
                  onReset={() => {
                    setDraftPrimary(null)
                    setDraftAdditional([])
                    schedule()
                  }}
                />
              </Stack>
            )}
          </Box>
        }
      />

      {addOpen && (
        <DrawerPopup
          title='Fields'
          fullScreen={false}
          /* The header X rather than 'Cancel'. Dismissing this picker without
             choosing genuinely aborts nothing-yet-done, so "Cancel" was not a lie
             here — but it sits directly over a sheet whose own dismissal is now an
             X, and two dismissals two rows apart wearing different labels is the
             ambiguity a stacked sheet must not have (§52). One word, one level. */
          handleOpen={addOpen}
          handleClose={() => setAddOpen(false)}
          paperSx={{ height: 'auto', maxHeight: '85vh' }}
          child={fieldList(unusedOptions, key => {
            setDraftAdditional(prev => [...prev, { field: key, direction: 'ASC' }])
            setAddOpen(false)
            schedule()
          })}
        />
      )}
    </>
  )
}

export default SortDrawer
